import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const AnalyticsApi = createApi({
	reducerPath: "Analytics",
	baseQuery: fetchBaseQuery({
		baseUrl: `${process.env.REACT_APP_BACKEND2_URL}/v1/analytics`,
		prepareHeaders: (headers) => {
			const token = localStorage.getItem("token");
			if (token) {
				headers.set("Authorization", `Bearer ${token}`);
			}
			return headers;
		},
		credentials: "include",
	}),
	endpoints: (builder) => ({
		getAnalyticsDatasets: builder.query({
			query: () => "/datasets",
		}),
		getAnalyticsDatasetFields: builder.query({
			query: (datasetId) => `/datasets/${datasetId}/fields`,
		}),
		getAnalyticsDatasetSubmitters: builder.query({
			query: (datasetId) => `/datasets/${datasetId}/submitters`,
		}),
		runAnalyticsQuery: builder.mutation({
			query: (body) => ({
				url: "/query",
				method: "POST",
				body,
			}),
		}),
		exportAnalyticsReport: builder.mutation({
			query: (body) => ({
				url: "/export",
				method: "POST",
				body,
				responseHandler: async (response) => {
					if (!response.ok) {
						const error = await response.text();
						throw new Error(error);
					}
					return response.blob();
				},
			}),
		}),
		getSavedReports: builder.query({
			query: () => "/saved-reports",
			providesTags: ["SavedReports"],
		}),
		getSavedReportById: builder.query({
			query: (id) => `/saved-reports/${id}`,
			providesTags: (_result, _error, id) => [{ type: "SavedReport", id }],
		}),
		getClientReports: builder.query({
			query: () => "/saved-reports/client",
			providesTags: ["SavedReports"],
		}),
		createSavedReport: builder.mutation({
			query: (body) => ({
				url: "/saved-reports",
				method: "POST",
				body,
			}),
			invalidatesTags: ["SavedReports"],
		}),
		updateSavedReport: builder.mutation({
			query: ({ id, ...body }) => ({
				url: `/saved-reports/${id}`,
				method: "PATCH",
				body,
			}),
			invalidatesTags: (_result, _error, { id }) => [
				"SavedReports",
				{ type: "SavedReport", id },
			],
		}),
		deleteSavedReport: builder.mutation({
			query: (id) => ({
				url: `/saved-reports/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["SavedReports"],
		}),
		getDashboards: builder.query({
			query: () => "/dashboards",
		}),
		createDashboard: builder.mutation({
			query: (body) => ({
				url: "/dashboards",
				method: "POST",
				body,
			}),
		}),
		updateDashboard: builder.mutation({
			query: ({ id, ...body }) => ({
				url: `/dashboards/${id}`,
				method: "PATCH",
				body,
			}),
		}),
		deleteDashboard: builder.mutation({
			query: (id) => ({
				url: `/dashboards/${id}`,
				method: "DELETE",
			}),
		}),
	}),
});

export const {
	useGetAnalyticsDatasetsQuery,
	useGetAnalyticsDatasetFieldsQuery,
	useGetAnalyticsDatasetSubmittersQuery,
	useRunAnalyticsQueryMutation,
	useExportAnalyticsReportMutation,
	useGetSavedReportsQuery,
	useGetSavedReportByIdQuery,
	useGetClientReportsQuery,
	useCreateSavedReportMutation,
	useUpdateSavedReportMutation,
	useDeleteSavedReportMutation,
	useGetDashboardsQuery,
	useCreateDashboardMutation,
	useUpdateDashboardMutation,
	useDeleteDashboardMutation,
} = AnalyticsApi;
